import { useCallback, useEffect, useRef, useState } from "react";
import P2PChatListView from "./menu-views/chat/p2p-chats/P2PChatListView";
import GroupChatsListView from "./menu-views/chat/group-chats/GroupChatsListView";

import SettingsView from "./menu-views/settings/SettingsView";
import {
  ChatUserAction,
  ActiveNavMenu,
  useNavigationState,
} from "@/zustand/NavigationState";
import CreateP2PChatView from "./action-views/createP2PChat/CreateP2PChatView";
import CreateGroupChatView from "./action-views/createGroupChat/CreateGroupChatView";
import SearchAIChatView from "./action-views/searchChat/SearchAIChatView";
import SearchP2PChatView from "./action-views/searchChat/SearchP2PChatView";
import SearchGroupChatView from "./action-views/searchChat/SearchGroupChatView";
import { useChatListState } from "@/zustand/ChatListState";
import NotificationListView from "./menu-views/notifications/NotificationListView";
import AnimatedGradientLine from "@/components/shared/AnimatedGradientLine";
import AiChatListView from "./menu-views/chat/ai-chats/AiChatListView";

const RESIZE_STORAGE_KEY = "leftColumnWidth";
const RESIZE_DEFAULT = 380;
const RESIZE_MIN = 280;
const RESIZE_MAX = 600;
const DESKTOP_MQ = "(min-width: 1024px)";

const readStoredWidth = (): number => {
  if (typeof window === "undefined") {
    return RESIZE_DEFAULT;
  }
  const raw = window.localStorage.getItem(RESIZE_STORAGE_KEY);
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  if (!Number.isFinite(n)) {
    return RESIZE_DEFAULT;
  }
  return Math.min(RESIZE_MAX, Math.max(RESIZE_MIN, n));
};

export default function LeftColumn() {
  const activeNavMenu = useNavigationState((state) => state.activeNavMenu);
  const activeChatUserAction = useNavigationState(
    (state) => state.activeChatUserAction,
  );

  const chatListSyncing = useChatListState((state) => state.chatListSyncing);

  // Resizable width on desktop only — mobile keeps its full-screen layout
  const [width, setWidth] = useState<number>(readStoredWidth);
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DESKTOP_MQ).matches
      : true,
  );
  const [isDragging, setIsDragging] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_MQ);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const onHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const startX = e.clientX;
    const startWidth = widthRef.current;
    const onMove = (ev: MouseEvent) => {
      const next = Math.min(
        RESIZE_MAX,
        Math.max(RESIZE_MIN, startWidth + (ev.clientX - startX)),
      );
      setWidth(next);
    };
    const onUp = () => {
      setIsDragging(false);
      window.localStorage.setItem(RESIZE_STORAGE_KEY, String(widthRef.current));
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  return (
    <div
      id="LeftColumn"
      // Desktop width is dynamic (resizable, via the style below), so the fixed
      // lg:w-leftColumnWidth class is dropped; main's safe-area + height-transition
      // polish is kept.
      className="fixed left-0 top-0 z-50 h-mobileLeftColumnHeight w-screen shrink-0 overflow-hidden bg-card pt-[env(safe-area-inset-top,0px)] transition-[height] duration-200 ease-out motion-reduce:transition-none lg:relative lg:h-full lg:pt-0"
      style={isDesktop ? { width } : undefined}
    >
      {/* chat list syncing animation */}
      {chatListSyncing && <AnimatedGradientLine position="top" zIndex={99} />}
      {activeNavMenu === ActiveNavMenu.NOTIFICATIONS && (
        <NotificationListView />
      )}
      {activeNavMenu === ActiveNavMenu.P2P_CHATS && <P2PChatListView />}
      {activeNavMenu === ActiveNavMenu.GROUP_CHATS && <GroupChatsListView />}
      {activeNavMenu === ActiveNavMenu.AI_CHAT && <AiChatListView />}
      {activeNavMenu === ActiveNavMenu.SETTINGS && <SettingsView />}
      {activeChatUserAction === ChatUserAction.CREATE_P2P_CHAT && (
        <CreateP2PChatView />
      )}
      {activeChatUserAction === ChatUserAction.CREATE_GROUP_CHAT && (
        <CreateGroupChatView />
      )}
      {activeChatUserAction === ChatUserAction.SEARCH_AI_CHAT && (
        <SearchAIChatView />
      )}
      {activeChatUserAction === ChatUserAction.SEARCH_P2P_CHAT && (
        <SearchP2PChatView />
      )}
      {activeChatUserAction === ChatUserAction.SEARCH_GROUP_CHAT && (
        <SearchGroupChatView />
      )}
      {isDesktop && (
        <div
          role="separator"
          aria-orientation="vertical"
          onMouseDown={onHandleMouseDown}
          onDoubleClick={() => {
            setWidth(RESIZE_DEFAULT);
            window.localStorage.setItem(
              RESIZE_STORAGE_KEY,
              String(RESIZE_DEFAULT),
            );
          }}
          className={
            "absolute right-0 top-0 z-50 h-full w-1 cursor-col-resize hover:bg-primary/40 " +
            (isDragging ? "bg-primary/60" : "bg-transparent")
          }
          title="Drag to resize · double-click to reset"
        />
      )}
    </div>
  );
}
